// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback } from 'react';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--rovo-expansion/rovo-chat-input-full-page';

import { MaxFullPageBanner } from '../../../common/components/max-full-page-banner';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

type MaxBannerMessageProps = {
	onTryMax?: () => void;
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<PlacementRenderProps>(),
		Component: ({ dismiss, maxSideNavBannerMessageProps }: PlacementRenderProps) => {
			const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();
			const { messageDismissed } = useMessageEvent();
			const { onTryMax } =
				(maxSideNavBannerMessageProps as MaxBannerMessageProps | undefined) ?? {};

			const handleDismiss = useCallback(async () => {
				await messageDismissed();
				void onClosed({
					interactionIdentifier: 'rovo-chat-long-running-task-fullpage-banner-dismiss',
					additionalMetadata: {
						maxIntroSuppressionSource: 'fullpage-banner-dismiss',
					},
				});

				dismiss();
			}, [dismiss, messageDismissed, onClosed]);

			const handleTryMax = useCallback(() => {
				try {
					void onCtaActioned({
						interactionIdentifier: 'rovo-chat-long-running-task-fullpage-banner-cta',
						additionalMetadata: {
							maxIntroSuppressionSource: 'fullpage-banner-cta',
						},
					});
				} finally {
					onTryMax?.();
					dismiss();
				}
			}, [dismiss, onCtaActioned, onTryMax]);

			return <MaxFullPageBanner onDismiss={handleDismiss} onTryMax={handleTryMax} />;
		},
	},
});
